/**
 * Global state management for the Intent Capture Modal
 *
 * Allows any component to trigger the modal with a specific intent,
 * which maps to a user-friendly title and service context.
 */

export interface IntentConfig {
  title: string
  service: string
  description?: string
  hideCompany?: boolean
}

// Map of intent identifiers to display configuration
const intentMap: Record<string, IntentConfig> = {
  // Connectivity intents
  'multi-site': {
    title: 'Connect Multiple Sites',
    service: 'Multi-site Connectivity',
    description: 'Get a solution for linking your business locations.',
  },
  'wifi': {
    title: 'Fix Slow Office WiFi',
    service: 'WiFi Solutions',
    description: 'Get a consultation on improving your wireless network.',
  },
  'new-office': {
    title: 'Set Up a New Office',
    service: 'New Office Setup',
    description: 'Get help planning connectivity for your new premises.',
  },
  'backup': {
    title: 'Get Backup Connectivity',
    service: 'Backup Internet',
    description: 'Ensure your business stays connected with redundant links.',
  },
  'upgrade': {
    title: 'Upgrade Office Broadband',
    service: 'Business Internet Upgrade',
    description: 'Find the right internet solution for your growing needs.',
  },
  'fibre': {
    title: 'Fibre Internet',
    service: 'Fibre Internet',
    description: 'Discuss fibre connectivity options for your business.',
  },

  // Security intents
  'warehouse-security': {
    title: 'Secure Your Warehouse',
    service: 'Warehouse Security',
    description: 'Get a customised security solution for your facility.',
  },
  'multi-site-security': {
    title: 'Monitor Multiple Sites',
    service: 'Multi-site Security Monitoring',
    description: 'Centralise security monitoring across your locations.',
  },
  'cctv-upgrade': {
    title: 'Upgrade Old Cameras',
    service: 'CCTV Upgrade',
    description: 'Modernise your surveillance system with HD cameras.',
  },
  'lone-worker': {
    title: 'Protect Staff Working Alone',
    service: 'Lone Worker Safety',
    description: 'Keep your remote workers safe with monitoring solutions.',
  },
  'cctv-sydney': {
    title: 'CCTV Installation (Sydney)',
    service: 'CCTV Sydney',
    description: 'Plan a commercial CCTV system tailored for your Sydney site.',
  },
  'cctv-brisbane': {
    title: 'CCTV Installation (Brisbane)',
    service: 'CCTV Brisbane',
    description: 'Plan a CCTV system tailored for Brisbane sites.',
  },
  'cctv-gold-coast': {
    title: 'CCTV Installation (Gold Coast)',
    service: 'CCTV Gold Coast',
    description: 'Plan a CCTV system tailored for Gold Coast sites.',
  },
  'access-control': {
    title: 'Access Control Systems',
    service: 'Access Control',
    description: 'Tell us about your doors and users and we\'ll recommend the right access control system.',
  },
  'access-control-sydney': {
    title: 'Access Control (Sydney)',
    service: 'Access Control Sydney',
    description: 'Secure your Sydney site with modern access control.',
  },
  'access-control-brisbane': {
    title: 'Access Control (Brisbane)',
    service: 'Access Control Brisbane',
    description: 'Secure your Brisbane site with modern access control.',
  },
  'access-control-gold-coast': {
    title: 'Access Control (Gold Coast)',
    service: 'Access Control Gold Coast',
    description: 'Secure your Gold Coast site with modern access control.',
  },
  'alarm-systems': {
    title: 'Commercial Alarm Systems',
    service: 'Commercial Alarm Systems',
    description: 'Tell us about your premises and we\'ll recommend the right commercial alarm system.',
  },
  'alarm-systems-brisbane': {
    title: 'Commercial Alarm Systems (Brisbane)',
    service: 'Commercial Alarm Systems Brisbane',
    description: 'Secure your Brisbane premises with a commercial alarm system.',
  },
  'alarm-systems-sydney': {
    title: 'Commercial Alarm Systems (Sydney)',
    service: 'Commercial Alarm Systems Sydney',
    description: 'Secure your Sydney premises with a commercial alarm system.',
  },
  'alarm-systems-gold-coast': {
    title: 'Commercial Alarm Systems (Gold Coast)',
    service: 'Commercial Alarm Systems Gold Coast',
    description: 'Secure your Gold Coast premises with a commercial alarm system.',
  },

  // Per-package commercial alarm intents (pricing cards). Company field shown.
  'alarm-systems-sydney-essential': {
    title: 'Essential Business Alarm (Sydney)',
    service: 'Commercial Alarm Systems Sydney (Essential)',
    description: 'Tell us about your premises and we\'ll quote the Essential Business Alarm for your Sydney site.',
  },
  'alarm-systems-sydney-smart': {
    title: 'Smart Business Alarm (Sydney)',
    service: 'Commercial Alarm Systems Sydney (Smart)',
    description: 'Tell us about your premises and we\'ll quote the Smart Business Alarm for your Sydney site.',
  },
  'alarm-systems-sydney-premium': {
    title: 'Premium Business Alarm (Sydney)',
    service: 'Commercial Alarm Systems Sydney (Premium)',
    description: 'Tell us about your premises and we\'ll quote the Premium Business Alarm for your Sydney site.',
  },
  'alarm-systems-brisbane-essential': {
    title: 'Essential Business Alarm (Brisbane)',
    service: 'Commercial Alarm Systems Brisbane (Essential)',
    description: 'Tell us about your premises and we\'ll quote the Essential Business Alarm for your Brisbane site.',
  },
  'alarm-systems-brisbane-smart': {
    title: 'Smart Business Alarm (Brisbane)',
    service: 'Commercial Alarm Systems Brisbane (Smart)',
    description: 'Tell us about your premises and we\'ll quote the Smart Business Alarm for your Brisbane site.',
  },
  'alarm-systems-brisbane-premium': {
    title: 'Premium Business Alarm (Brisbane)',
    service: 'Commercial Alarm Systems Brisbane (Premium)',
    description: 'Tell us about your premises and we\'ll quote the Premium Business Alarm for your Brisbane site.',
  },
  'alarm-systems-gold-coast-essential': {
    title: 'Essential Business Alarm (Gold Coast)',
    service: 'Commercial Alarm Systems Gold Coast (Essential)',
    description: 'Tell us about your premises and we\'ll quote the Essential Business Alarm for your Gold Coast site.',
  },
  'alarm-systems-gold-coast-smart': {
    title: 'Smart Business Alarm (Gold Coast)',
    service: 'Commercial Alarm Systems Gold Coast (Smart)',
    description: 'Tell us about your premises and we\'ll quote the Smart Business Alarm for your Gold Coast site.',
  },
  'alarm-systems-gold-coast-premium': {
    title: 'Premium Business Alarm (Gold Coast)',
    service: 'Commercial Alarm Systems Gold Coast (Premium)',
    description: 'Tell us about your premises and we\'ll quote the Premium Business Alarm for your Gold Coast site.',
  },
  // National hub per-tier intents (/alarm-systems pricing cards).
  'alarm-systems-essential': {
    title: 'Essential Business Alarm',
    service: 'Commercial Alarm Systems (Essential)',
    description: 'Tell us about your premises and we\'ll quote the Essential Business Alarm for your site.',
  },
  'alarm-systems-smart': {
    title: 'Smart Business Alarm',
    service: 'Commercial Alarm Systems (Smart)',
    description: 'Tell us about your premises and we\'ll quote the Smart Business Alarm for your site.',
  },
  'alarm-systems-premium': {
    title: 'Premium Business Alarm',
    service: 'Commercial Alarm Systems (Premium)',
    description: 'Tell us about your premises and we\'ll quote the Premium Business Alarm for your site.',
  },
  'alarm-monitoring-brisbane': {
    title: 'Alarm Monitoring (Brisbane)',
    service: 'Alarm Monitoring Brisbane',
    description: 'Connect your Brisbane alarm to 24/7 back-to-base monitoring.',
  },
  'alarm-monitoring-sydney': {
    title: 'Alarm Monitoring (Sydney)',
    service: 'Alarm Monitoring Sydney',
    description: 'Connect your Sydney alarm to 24/7 back-to-base monitoring.',
  },
  'alarm-monitoring-gold-coast': {
    title: 'Alarm Monitoring (Gold Coast)',
    service: 'Alarm Monitoring Gold Coast',
    description: 'Connect your Gold Coast alarm to 24/7 back-to-base monitoring.',
  },
  'intercom-systems-brisbane': {
    title: 'Intercom Systems (Brisbane)',
    service: 'Intercom Systems Brisbane',
    description: 'Discuss commercial intercom options for your Brisbane site.',
  },
  'intercom-systems-sydney': {
    title: 'Intercom Systems (Sydney)',
    service: 'Intercom Systems Sydney',
    description: 'Discuss commercial intercom options for your Sydney site.',
  },
  'intercom-systems-gold-coast': {
    title: 'Intercom Systems (Gold Coast)',
    service: 'Intercom Systems Gold Coast',
    description: 'Discuss commercial intercom options for your Gold Coast site.',
  },
  'property-management': {
    title: 'Property Management Security',
    service: 'Property Management',
    description: 'Upgrade security across managed properties.',
  },
  'childcare-education-security': {
    title: 'Childcare & Education Security',
    service: 'Childcare & Education Security',
    description: 'Design a compliant security plan for your centre.',
  },

  // School security intents (national hub + per-city LPs). B2B, company field shown.
  'school-security': {
    title: 'Book a School Security Site Assessment',
    service: 'School Security Systems',
    description: 'Tell us about your school and we\'ll design a practical security scope and quote.',
  },
  'school-security-sydney': {
    title: 'Book a School Security Site Assessment (Sydney)',
    service: 'School Security Systems Sydney',
    description: 'Tell us about your Sydney school and we\'ll design a practical security scope and quote.',
  },
  'school-security-brisbane': {
    title: 'Book a School Security Site Assessment (Brisbane)',
    service: 'School Security Systems Brisbane',
    description: 'Tell us about your Brisbane school and we\'ll design a practical security scope and quote.',
  },
  'school-security-gold-coast': {
    title: 'Book a School Security Site Assessment (Gold Coast)',
    service: 'School Security Systems Gold Coast',
    description: 'Tell us about your Gold Coast school and we\'ll design a practical security scope and quote.',
  },
  'jobsite-surveillance': {
    title: 'Jobsite Surveillance',
    service: 'Jobsite Surveillance',
    description: 'Deploy rapid surveillance for construction sites.',
  },
  'gym-fitness-security': {
    title: 'Gym & Fitness Security',
    service: 'Gym & Fitness Security',
    description: 'Protect gyms with access control and monitoring.',
  },
  'residential-cctv-quote': {
    title: 'Get a Free Residential CCTV Quote',
    service: 'Residential CCTV',
    description: 'Tell us about your home and we\'ll prepare a tailored quote.',
    hideCompany: true,
  },
  'residential-cctv-sydney': {
    title: 'Get a Free Residential CCTV Quote (Sydney)',
    service: 'Residential CCTV Sydney',
    description: 'Tell us about your Sydney home and we\'ll prepare a tailored quote.',
    hideCompany: true,
  },
  'residential-cctv-brisbane': {
    title: 'Get a Free Residential CCTV Quote (Brisbane)',
    service: 'Residential CCTV Brisbane',
    description: 'Tell us about your Brisbane home and we\'ll prepare a tailored quote.',
    hideCompany: true,
  },
  'residential-cctv-gold-coast': {
    title: 'Get a Free Residential CCTV Quote (Gold Coast)',
    service: 'Residential CCTV Gold Coast',
    description: 'Tell us about your Gold Coast home and we\'ll prepare a tailored quote.',
    hideCompany: true,
  },

  // Residential alarm intents (hub + per-city LPs). Company field hidden (homeowners).
  'residential-alarm-systems': {
    title: 'Get a Free Home Alarm Quote',
    service: 'Residential Alarm Systems',
    description: 'Tell us about your home and we\'ll quote the right alarm package.',
    hideCompany: true,
  },
  'residential-alarm-systems-sydney': {
    title: 'Get a Free Home Alarm Quote (Sydney)',
    service: 'Residential Alarm Systems Sydney',
    description: 'Tell us about your Sydney home and we\'ll quote the right alarm package.',
    hideCompany: true,
  },
  'residential-alarm-systems-brisbane': {
    title: 'Get a Free Home Alarm Quote (Brisbane)',
    service: 'Residential Alarm Systems Brisbane',
    description: 'Tell us about your Brisbane home and we\'ll quote the right alarm package.',
    hideCompany: true,
  },
  'residential-alarm-systems-gold-coast': {
    title: 'Get a Free Home Alarm Quote (Gold Coast)',
    service: 'Residential Alarm Systems Gold Coast',
    description: 'Tell us about your Gold Coast home and we\'ll quote the right alarm package.',
    hideCompany: true,
  },

  // Residential alarm monitoring intents (hub + per-city LPs). Company field hidden (homeowners).
  'residential-alarm-monitoring': {
    title: 'Get a Free Home Alarm Monitoring Quote',
    service: 'Residential Alarm Monitoring',
    description: 'Tell us about your home alarm and we\'ll set up 24/7 back-to-base monitoring.',
    hideCompany: true,
  },
  'residential-alarm-monitoring-sydney': {
    title: 'Get a Free Home Alarm Monitoring Quote (Sydney)',
    service: 'Residential Alarm Monitoring Sydney',
    description: 'Tell us about your Sydney home alarm and we\'ll set up 24/7 back-to-base monitoring.',
    hideCompany: true,
  },
  'residential-alarm-monitoring-brisbane': {
    title: 'Get a Free Home Alarm Monitoring Quote (Brisbane)',
    service: 'Residential Alarm Monitoring Brisbane',
    description: 'Tell us about your Brisbane home alarm and we\'ll set up 24/7 back-to-base monitoring.',
    hideCompany: true,
  },
  'residential-alarm-monitoring-gold-coast': {
    title: 'Get a Free Home Alarm Monitoring Quote (Gold Coast)',
    service: 'Residential Alarm Monitoring Gold Coast',
    description: 'Tell us about your Gold Coast home alarm and we\'ll set up 24/7 back-to-base monitoring.',
    hideCompany: true,
  },

  // Residential intercom intents (hub + per-city LPs). Company field hidden (homeowners).
  'residential-intercom': {
    title: 'Get a Free Home Intercom Quote',
    service: 'Residential Intercom',
    description: 'Tell us about your door or gate and we\'ll quote the right home intercom package.',
    hideCompany: true,
  },
  'residential-intercom-sydney': {
    title: 'Get a Free Home Intercom Quote (Sydney)',
    service: 'Residential Intercom Sydney',
    description: 'Tell us about your Sydney home and we\'ll quote the right intercom package.',
    hideCompany: true,
  },
  'residential-intercom-brisbane': {
    title: 'Get a Free Home Intercom Quote (Brisbane)',
    service: 'Residential Intercom Brisbane',
    description: 'Tell us about your Brisbane home and we\'ll quote the right intercom package.',
    hideCompany: true,
  },
  'residential-intercom-gold-coast': {
    title: 'Get a Free Home Intercom Quote (Gold Coast)',
    service: 'Residential Intercom Gold Coast',
    description: 'Tell us about your Gold Coast home and we\'ll quote the right intercom package.',
    hideCompany: true,
  },

  // Managed IT intents
  'reduce-downtime': {
    title: 'Reduce IT Downtime',
    service: 'Managed IT Support',
    description: 'Get proactive IT support to minimise disruptions.',
  },
  'outsource-it': {
    title: 'Outsource Your IT',
    service: 'Managed IT Services',
    description: 'Let our Australian team handle your IT operations.',
  },
  'it-strategy': {
    title: 'Plan IT for Growth',
    service: 'IT Strategy',
    description: 'Get expert guidance on scaling your technology.',
  },

  // General intents
  'free-quote': {
    title: 'Get a Free Quote',
    service: 'General Enquiry',
    description: 'Tell us about your requirements and we\'ll prepare a tailored quote.',
  },
  'consultation': {
    title: 'Schedule Your Free Consultation',
    service: 'Consultation Request',
    description: 'Book a time to discuss your requirements with our team.',
  },
  'assessment': {
    title: 'Book Your Free Assessment',
    service: 'Site Assessment',
    description: 'We\'ll assess your needs and recommend the right solution.',
  },
  'security-review': {
    title: 'Schedule a Security Review',
    service: 'Security Review',
    description: 'Book an engineer to review your current alarm, access, and monitoring setup.',
  },
  'support': {
    title: 'Contact Support',
    service: 'Support Request',
    description: 'Get help from our Australian-based support team.',
  },
  'demo': {
    title: 'Schedule a Demo',
    service: 'Demo Request',
    description: 'See our solutions in action with a personalised demonstration.',
  },

  // Support intents (service-specific)
  'support-broadband': {
    title: 'Broadband Support',
    service: 'Broadband Support',
    description: 'Get help with your NBN or internet connection.',
  },
  'support-billing': {
    title: 'Billing Support',
    service: 'Billing Support',
    description: 'Get help with invoices, payments, or account queries.',
  },
  'support-cctv': {
    title: 'CCTV Support',
    service: 'CCTV Support',
    description: 'Get help with your security camera system.',
  },
  'support-voice': {
    title: 'Voice Support',
    service: 'Voice Support',
    description: 'Get help with your phone or VoIP system.',
  },
  'support-access-control': {
    title: 'Access Control Support',
    service: 'Access Control Support',
    description: 'Get help with your access control system.',
  },
  'support-alarms': {
    title: 'Alarm Support',
    service: 'Alarm Support',
    description: 'Get help with your alarm or monitoring system.',
  },
  'support-managed-it': {
    title: 'IT Support',
    service: 'Managed IT Support',
    description: 'Get help from our Australian-based IT support team.',
  },
}

// Global state (shared across components via Nuxt auto-import)
const isOpen = ref(false)
const currentIntent = ref<string | null>(null)

export function useIntentModal() {
  /**
   * Open the modal with a specific intent identifier
   */
  function open(intent: string) {
    currentIntent.value = intent
    isOpen.value = true
  }

  /**
   * Close the modal and reset state
   */
  function close() {
    isOpen.value = false
    // Delay clearing intent to allow for closing animation
    setTimeout(() => {
      currentIntent.value = null
    }, 200)
  }

  /**
   * Get the configuration for the current intent
   */
  const config = computed<IntentConfig | null>(() => {
    if (!currentIntent.value) return null
    return intentMap[currentIntent.value] ?? {
      title: 'Get in Touch',
      service: currentIntent.value,
    }
  })

  /**
   * Resolve the quote-style intent for a CTA. Upgrades the generic 'free-quote'
   * to the 'residential-cctv-quote' variant (Company field hidden) when the
   * service context indicates a residential page. Pass-through for any other intent.
   */
  function resolveQuoteIntent(intent: string, service?: string): string {
    if (intent === 'free-quote' && (service ?? '').toLowerCase().includes('residential')) {
      return 'residential-cctv-quote'
    }
    return intent
  }

  /**
   * Check if a URL is an intent-capture link
   * Matches: /contact?need=xxx, /contact?type=xxx, /quote
   */
  function isIntentLink(href: string): boolean {
    return (
      href.startsWith('/contact?need=') ||
      href.startsWith('/contact?type=') ||
      href === '/quote'
    )
  }

  /**
   * Extract the intent identifier from a URL
   * - /contact?need=xxx → xxx
   * - /contact?type=consultation → consultation
   * - /contact?type=demo → demo
   * - /quote → free-quote
   */
  function extractIntent(href: string): string | null {
    // Handle /quote → free-quote
    if (href === '/quote') {
      return 'free-quote'
    }
    // Handle /contact?need=xxx
    const needMatch = href.match(/[?&]need=([^&]+)/)
    if (needMatch) {
      return needMatch[1]
    }
    // Handle /contact?type=xxx
    const typeMatch = href.match(/[?&]type=([^&]+)/)
    if (typeMatch) {
      return typeMatch[1]
    }
    return null
  }

  /**
   * Handle a potential intent link - returns true if handled, false if should navigate normally
   */
  function handleLink(href: string): boolean {
    if (!isIntentLink(href)) return false
    const intent = extractIntent(href)
    if (intent) {
      open(intent)
      return true
    }
    return false
  }

  return {
    isOpen,
    currentIntent,
    config,
    open,
    close,
    resolveQuoteIntent,
    isIntentLink,
    extractIntent,
    handleLink,
  }
}
